function isFunction(handler) {
  return typeof handler === 'function';
}

function sanitizeContext(value, ancestors = []) {
  if (!value || typeof value !== 'object') {
    return value;
  }

  if (ancestors.indexOf(value) !== -1) {
    return undefined;
  }

  const parents = ancestors.concat([value]);

  if (Array.isArray(value)) {
    return value.map(item => sanitizeContext(item, parents));
  }

  const sanitized = value instanceof Error ? new Error(value.message) : {};

  if (value instanceof Error) {
    sanitized.name = value.name;
    sanitized.stack = value.stack;
  }

  Object.keys(value).forEach(key => {
    const normalizedKey = key.replace(/[^a-z0-9]/gi, '').toLowerCase();

    if (['userhash', 'sessionuserhash', 'profileuserhash', 'profileid'].indexOf(normalizedKey) !== -1
      || key === '__proto__') {
      return;
    }

    sanitized[key] = sanitizeContext(value[key], parents);
  });

  return sanitized;
}

export function defaultLoadScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('Document is not available'));
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = src;

    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load Datadog script: ${src}`));

    document.head.appendChild(script);
  });
}

class Datadog {

  constructor() {
    this._initialized = false;
    this._logsInitialized = false;
    this._disabled = false;
    this._globalContext = {};
    this._scriptPromises = {};
    this._initializationPromise = null;
    this._pendingOperations = [];
  }

  getRum() {
    return typeof window !== 'undefined' && window.DD_RUM ? window.DD_RUM : null;
  }

  getLogs() {
    return typeof window !== 'undefined' && window.DD_LOGS ? window.DD_LOGS : null;
  }

  disable() {
    this._disabled = true;
    this._pendingOperations = [];
  }

  enable() {
    this._disabled = false;
  }

  isDisabled() {
    return this._disabled;
  }

  runOrQueue(operation) {
    if (!isFunction(operation) || this._disabled) {
      return false;
    }

    if (!this._initialized) {
      this._pendingOperations.push(operation);
      return false;
    }

    operation();
    return true;
  }

  flushPendingOperations() {
    if (this._disabled || !this._initialized || this._pendingOperations.length === 0) {
      return;
    }

    const operations = this._pendingOperations.slice();
    this._pendingOperations = [];

    operations.forEach(operation => {
      try {
        operation();
      } catch (error) {
        console.error('Datadog flushPendingOperations', error);
      }
    });
  }

  getGlobalContext() {
    return { ...this._globalContext };
  }

  getSiteKey(site = 'datadoghq.com') {
    const normalizedSite = `${site}`.toLowerCase();
    const siteMap = {
      'app.datadoghq.com': 'us1',
      'datadoghq.com': 'us1',
      'app.datadoghq.eu': 'eu',
      'datadoghq.eu': 'eu',
      'ap1.datadoghq.com': 'ap1',
      'ap2.datadoghq.com': 'ap2',
      'us3.datadoghq.com': 'us3',
      'us5.datadoghq.com': 'us5',
      'app.ddog-gov.com': 'gov',
      'us2.ddog-gov.com': 'gov',
      'ddog-gov.com': 'gov',
      us1: 'us1',
      eu: 'eu',
      ap1: 'ap1',
      ap2: 'ap2',
      us3: 'us3',
      us5: 'us5',
      gov: 'gov',
    };

    return siteMap[normalizedSite] || 'us1';
  }

  getRumCdnUrl({ site = 'datadoghq.com', version = 'v7', overrideUrl = '' } = {}) {
    if (overrideUrl) {
      return overrideUrl;
    }

    const siteKey = this.getSiteKey(site);

    if (siteKey === 'gov') {
      return `https://www.datadoghq-browser-agent.com/datadog-rum-${version}.js`;
    }

    return `https://www.datadoghq-browser-agent.com/${siteKey}/${version}/datadog-rum.js`;
  }

  getLogsCdnUrl({ site = 'datadoghq.com', version = 'v6', overrideUrl = '' } = {}) {
    if (overrideUrl) {
      return overrideUrl;
    }

    const siteKey = this.getSiteKey(site);

    if (siteKey === 'gov') {
      return `https://www.datadoghq-browser-agent.com/datadog-logs-${version}.js`;
    }

    return `https://www.datadoghq-browser-agent.com/${siteKey}/${version}/datadog-logs.js`;
  }

  loadScript(src, loadScript = defaultLoadScript) {
    if (!src) {
      return Promise.resolve(false);
    }

    if (this._scriptPromises[src]) {
      return this._scriptPromises[src];
    }

    this._scriptPromises[src] = loadScript(src)
      .then(result => result)
      .catch(error => {
        delete this._scriptPromises[src];
        throw error;
      });

    return this._scriptPromises[src];
  }

  waitForClient(getClient, timeoutMs = 4000) {
    return new Promise((resolve, reject) => {
      const startedAt = Date.now();

      const pollClient = () => {
        const client = getClient();

        if (client) {
          resolve(client);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          reject(new Error('Timed out waiting for Datadog client'));
          return;
        }

        setTimeout(pollClient, 50);
      };

      pollClient();
    });
  }

  loadClients({
    site,
    rumVersion,
    logsVersion,
    rumCdnUrl,
    logsCdnUrl,
    loadLogs = false,
    loadScript,
    timeoutMs,
  } = {}) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.resolve(false);
    }

    const loaders = [
      this.loadScript(
        this.getRumCdnUrl({
          site,
          version: rumVersion,
          overrideUrl: rumCdnUrl,
        }),
        loadScript
      ).then(() => this.waitForClient(() => this.getRum(), timeoutMs)),
    ];

    if (loadLogs) {
      loaders.push(
        this.loadScript(
          this.getLogsCdnUrl({
            site,
            version: logsVersion,
            overrideUrl: logsCdnUrl,
          }),
          loadScript
        ).then(() => this.waitForClient(() => this.getLogs(), timeoutMs))
      );
    }

    return Promise.all(loaders);
  }

  async initialize({ rum = null, logs = null, cdn = {} } = {}) {
    if (this._disabled || !rum || !rum.applicationId || !rum.clientToken) {
      return false;
    }

    if (this._initialized) {
      return true;
    }

    if (this._initializationPromise) {
      return this._initializationPromise;
    }

    const initializationPromise = this.loadClients({
      site: rum.site,
      rumVersion: cdn.rumVersion,
      logsVersion: cdn.logsVersion,
      rumCdnUrl: cdn.rumCdnUrl,
      logsCdnUrl: cdn.logsCdnUrl,
      loadLogs: Boolean(logs && logs.enabled),
      loadScript: cdn.loadScript,
      timeoutMs: cdn.timeoutMs,
    })
      .then(() => {
        const rumClient = this.getRum();

        if (!rumClient || !isFunction(rumClient.init)) {
          return false;
        }

        rumClient.init(rum);

        if (rum.forceSessionReplayRecording && isFunction(rumClient.startSessionReplayRecording)) {
          rumClient.startSessionReplayRecording({ force: true });
        }

        if (logs && logs.enabled) {
          const logsClient = this.getLogs();

          if (logsClient && isFunction(logsClient.init)) {
            const { enabled, ...logsConfiguration } = logs;
            logsClient.init(logsConfiguration);
            this._logsInitialized = true;
          }
        }

        this._initialized = true;
        this.flushPendingOperations();
        return true;
      })
      .catch(error => {
        console.error('Datadog initialize', error);
        return false;
      })
      .then(result => {
        if (!result) {
          this._initializationPromise = null;
        }

        return result;
      });

    this._initializationPromise = initializationPromise;

    return initializationPromise;
  }

  init(configuration = {}) {
    return this.initialize({ rum: configuration });
  }

  isInitialized() {
    return this._initialized;
  }

  setUser(user = {}) {
    const safeUser = sanitizeContext(user);
    this.runOrQueue(() => {
      const rum = this.getRum();
      const logs = this.getLogs();

      try {
        if (rum && isFunction(rum.setUser)) {
          rum.setUser(safeUser);
        }

        if (this._logsInitialized && logs && isFunction(logs.setUser)) {
          logs.setUser(safeUser);
        }
      } catch (error) {
        console.error('Datadog setUser', error);
      }
    });
  }

  clearUser() {
    this.runOrQueue(() => {
      const rum = this.getRum();
      const logs = this.getLogs();

      try {
        if (rum && isFunction(rum.clearUser)) {
          rum.clearUser();
        }

        if (this._logsInitialized && logs && isFunction(logs.clearUser)) {
          logs.clearUser();
        }
      } catch (error) {
        console.error('Datadog clearUser', error);
      }
    });
  }

  setGlobalContext(context = {}) {
    const safeContext = sanitizeContext(context);
    this.runOrQueue(() => {
      const rum = this.getRum();
      const logs = this.getLogs();

      try {
        this._globalContext = {
          ...this._globalContext,
          ...safeContext,
        };

        const mergedContext = this.getGlobalContext();

        if (rum && isFunction(rum.setGlobalContext)) {
          rum.setGlobalContext(mergedContext);
        } else if (rum && isFunction(rum.setGlobalContextProperty)) {
          Object.keys(safeContext).forEach(key => {
            rum.setGlobalContextProperty(key, safeContext[key]);
          });
        }

        if (this._logsInitialized && logs && isFunction(logs.setGlobalContext)) {
          logs.setGlobalContext(mergedContext);
        } else if (this._logsInitialized && logs && isFunction(logs.setGlobalContextProperty)) {
          Object.keys(safeContext).forEach(key => {
            logs.setGlobalContextProperty(key, safeContext[key]);
          });
        }
      } catch (error) {
        console.error('Datadog setGlobalContext', error);
      }
    });
  }

  setGlobalContextProperty(key, value) {
    if (!key) {
      return;
    }

    const safeContext = sanitizeContext({ [key]: value });
    if (!Object.prototype.hasOwnProperty.call(safeContext, key)) {
      return;
    }
    const safeValue = safeContext[key];

    this.runOrQueue(() => {
      const rum = this.getRum();
      const logs = this.getLogs();

      try {
        this._globalContext[key] = safeValue;

        if (rum && isFunction(rum.setGlobalContextProperty)) {
          rum.setGlobalContextProperty(key, safeValue);
        } else if (rum && isFunction(rum.setGlobalContext)) {
          rum.setGlobalContext(this.getGlobalContext());
        }

        if (this._logsInitialized && logs && isFunction(logs.setGlobalContextProperty)) {
          logs.setGlobalContextProperty(key, safeValue);
        } else if (this._logsInitialized && logs && isFunction(logs.setGlobalContext)) {
          logs.setGlobalContext(this.getGlobalContext());
        }
      } catch (error) {
        console.error('Datadog setGlobalContextProperty', error);
      }
    });
  }

  removeGlobalContext(keys = []) {
    if (!Array.isArray(keys) || keys.length === 0) {
      return;
    }

    this.runOrQueue(() => {
      const rum = this.getRum();
      const logs = this.getLogs();

      try {
        keys.forEach(key => {
          delete this._globalContext[key];
        });

        if (rum && isFunction(rum.removeGlobalContextProperty)) {
          keys.forEach(key => {
            rum.removeGlobalContextProperty(key);
          });
        } else if (rum && isFunction(rum.setGlobalContextProperty)) {
          keys.forEach(key => {
            rum.setGlobalContextProperty(key, null);
          });
        }

        if (this._logsInitialized && logs && isFunction(logs.removeGlobalContextProperty)) {
          keys.forEach(key => {
            logs.removeGlobalContextProperty(key);
          });
        } else if (this._logsInitialized && logs && isFunction(logs.setGlobalContextProperty)) {
          keys.forEach(key => {
            logs.setGlobalContextProperty(key, null);
          });
        }
      } catch (error) {
        console.error('Datadog removeGlobalContext', error);
      }
    });
  }

  removeGlobalContextProperty(key) {
    if (!key) {
      return;
    }

    this.removeGlobalContext([key]);
  }

  addAction(name, context = {}) {
    const safeContext = sanitizeContext(context);
    this.runOrQueue(() => {
      const rum = this.getRum();

      if (!rum || !isFunction(rum.addAction)) {
        return;
      }

      try {
        rum.addAction(name, safeContext);
      } catch (error) {
        console.error('Datadog addAction', error);
      }
    });
  }

  recordCustomEvent(name, context = {}) {
    this.addAction(name, context);
  }

  addError(error, context = {}) {
    const safeError = sanitizeContext(error);
    const safeContext = sanitizeContext(context);
    this.runOrQueue(() => {
      const rum = this.getRum();

      if (!rum || !isFunction(rum.addError)) {
        return;
      }

      try {
        rum.addError(safeError, safeContext);
      } catch (errorToReport) {
        console.error('Datadog addError', errorToReport);
      }
    });
  }

  logInfo(message, context = {}) {
    const safeMessage = sanitizeContext(message);
    const safeContext = sanitizeContext(context);
    this.runOrQueue(() => {
      const logs = this.getLogs();

      if (!this._logsInitialized || !logs || !logs.logger || !isFunction(logs.logger.info)) {
        return;
      }

      try {
        logs.logger.info(safeMessage, safeContext);
      } catch (error) {
        console.error('Datadog logInfo', error);
      }
    });
  }

  logWarn(message, context = {}) {
    const safeMessage = sanitizeContext(message);
    const safeContext = sanitizeContext(context);
    this.runOrQueue(() => {
      const logs = this.getLogs();

      if (!this._logsInitialized || !logs || !logs.logger || !isFunction(logs.logger.warn)) {
        return;
      }

      try {
        logs.logger.warn(safeMessage, safeContext);
      } catch (error) {
        console.error('Datadog logWarn', error);
      }
    });
  }

  logError(message, context = {}) {
    const safeMessage = sanitizeContext(message);
    const safeContext = sanitizeContext(context);
    this.runOrQueue(() => {
      const logs = this.getLogs();

      if (!this._logsInitialized || !logs || !logs.logger || !isFunction(logs.logger.error)) {
        return;
      }

      try {
        logs.logger.error(safeMessage, safeContext);
      } catch (error) {
        console.error('Datadog logError', error);
      }
    });
  }

  trackRoute(pathname = '', action = 'LOAD') {
    if (!pathname) {
      return;
    }

    this.setGlobalContextProperty('current_route', pathname);
    this.setGlobalContextProperty('navigation_action', action);
  }
}

export { Datadog };
export default new Datadog();


// WEBPACK FOOTER //
// ./src/utils/trackers/Datadog.js